import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { EngineDashboard } from '@/components/EngineDashboard';
import { InsightText } from '@/components/InsightText';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import {
  fetchSeoIntelligence,
  fetchSeoProjectOptions,
  fetchSeoSearchAnalyticsWindow,
  syncSeoSearchAnalytics,
  type SeoGscInsight,
  type SeoSearchQueryRow,
} from '@/data/sources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';
import {
  GSC_PRESETS,
  aggregateGscRows,
  filterGscRows,
  gscRowKey,
  gscTotals,
  presetFromParam,
  ukDate,
  type GscPreset,
} from '@/data/gsc-table-model';

/* /seo/search-performance/google-search-console — the Search Performance
 * landing (G1/B3). Stored Google Search Console query metrics for one project:
 * totals row, top-queries table, Sync Now (POST search-analytics/sync) and ONE
 * stored top-opportunity AI insight rendered in the Copilot advisory treatment.
 * Evidence-first honesty throughout: every number is a stored, source-
 * attributed GSC row ("Source: Google Search Console" is always visible);
 * 503 provider_unavailable renders as an "awaiting connection" panel (same
 * posture as Site Health); no rows renders a teach-style empty state — never
 * blank, never invented. The insight is advisory and platform-branded
 * (BookedCentral Copilot) — the underlying provider/model never reaches the
 * customer.
 *
 * fix/gsc-table-filters (02-11-2026): date presets (Last day / 7 days / 28 days
 * / 3 months, URL ?range=), query search (URL ?q=), every column sortable,
 * one stored window per view aggregated by query, and rows keyed by query +
 * window (the old query-only key gave React duplicate keys -> ghost rows and
 * a table that looked unsorted). */

const SOURCE_TAG = 'Source: Google Search Console';

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** CTR fraction → display percentage (two decimals under 1%, one above). */
function formatCtr(ctr: number): string {
  const pct = ctr * 100;
  return `${pct.toFixed(pct > 0 && pct < 1 ? 2 : 1)}%`;
}

/** The contract's top-opportunity rule (highest impressions with position>10)
 *  applied client-side ONLY to describe which stored metrics an insight was
 *  based on — display context, never a new claim. */
function topOpportunityRow(rows: SeoSearchQueryRow[]): SeoSearchQueryRow | null {
  let top: SeoSearchQueryRow | null = null;
  for (const r of rows) {
    if (r.position > 10 && (top === null || r.impressions > top.impressions)) top = r;
  }
  return top;
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-lg font-semibold text-text-primary" data-tabular>
        {value}
      </div>
    </div>
  );
}

/** The AI insight strip — the CopilotRail advisory treatment on a panel:
 *  copper keyline wash, "proposed by copilot" eyebrow, explicit Advisory chip,
 *  the stored insight body, a "based on" evidence line, and platform-only
 *  identity in the footer. */
function InsightStrip({ insight, basedOn }: { insight: SeoGscInsight; basedOn: string }) {
  return (
    <div className="rounded-md border border-ai/40 bg-ai-wash/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
          ✧ proposed by copilot
        </span>
        <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
          Advisory
        </span>
      </div>

      <div className="text-sm font-medium text-text-primary">{insight.subject}</div>
      <InsightText text={insight.body} className="mt-1 text-sm text-text-primary" />

      <p className="mt-2 text-[11px] leading-snug text-text-secondary">
        <span className="font-semibold text-text-tertiary">Based on · </span>
        {basedOn}
      </p>

      <div className="mt-3 flex items-center justify-between border-t border-ai/15 pt-2">
        <span className="text-[10px] text-text-tertiary">Advisory only · I propose, you decide</span>
        {/* platform branding only — never the underlying provider/model */}
        <span className="font-mono text-[9px] text-text-tertiary">BookedCentral Copilot</span>
      </div>
    </div>
  );
}

interface InlinePanel {
  title: string;
  body: string;
}

const NUM_CELL = 'font-mono text-text-secondary';

/** Clicks/Impressions/CTR/Position are sortable (client-side over the stored,
 *  already-loaded rows — free, no re-sync). Every GSC metric is a real
 *  returned number, so all four sort honestly. Query sorts A–Z
 *  (fix/gsc-table-filters). */
const GSC_COLUMNS: SortableColumn<SeoSearchQueryRow>[] = [
  {
    key: 'query',
    header: 'Query',
    sortable: true,
    sortValue: (r) => r.query.toLowerCase(),
    render: (r) => r.query,
    truncate: true,
    maxWidthClass: 'max-w-[280px]',
    title: (r) => r.query,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'clicks',
    header: 'Clicks',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.clicks,
    render: (r) => formatInt(r.clicks),
    cellClassName: `${NUM_CELL} text-text-primary`,
  },
  {
    key: 'impressions',
    header: 'Impressions',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.impressions,
    render: (r) => formatInt(r.impressions),
    cellClassName: `${NUM_CELL} text-text-primary`,
  },
  {
    key: 'ctr',
    header: 'CTR',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.ctr,
    render: (r) => formatCtr(r.ctr),
    cellClassName: NUM_CELL,
  },
  {
    key: 'position',
    header: 'Position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (r) => r.position,
    render: (r) => r.position.toFixed(1),
    cellClassName: NUM_CELL,
  },
];

export function SeoSearchConsolePage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [syncing, setSyncing] = useState(false);
  // Date preset + search live in the URL so a reload / shared link keeps them.
  const [searchParams, setSearchParams] = useSearchParams();
  const preset: GscPreset = presetFromParam(searchParams.get('range'));
  const search = searchParams.get('q') ?? '';
  const setParam = (key: 'range' | 'q', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || (key === 'range' && value === '28d')) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const presetMeta = GSC_PRESETS.find((p) => p.token === preset) ?? GSC_PRESETS[2];
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<InlinePanel | null>(null);
  /** Insight returned by THIS session's sync, keyed by project so switching
   *  projects never shows another property's insight. */
  const [syncedInsight, setSyncedInsight] = useState<{
    projectId: string;
    insight: SeoGscInsight;
  } | null>(null);

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  const projectId = resolveActiveProjectId(active, projects);
  const project = projects.find((p) => p.id === projectId) ?? null;

  const analyticsQ = useQuery({
    queryKey: ['seo', 'search-analytics', projectId, preset],
    queryFn: () => fetchSeoSearchAnalyticsWindow(projectId as string, preset),
    enabled: projectId !== null,
  });

  // Workspace rollup — the stored top-opportunity insight (bc_seo_insights)
  // for this project when it is the workspace's current top opportunity.
  const intelligenceQ = useQuery({
    queryKey: ['seo', 'intelligence'],
    queryFn: fetchSeoIntelligence,
  });

  const clearFeedback = () => {
    setNotice(null);
    setPanel(null);
  };

  const handleSync = async () => {
    if (projectId === null || syncing) return;
    clearFeedback();
    setSyncing(true);
    try {
      const res = await syncSeoSearchAnalytics(projectId, preset);
      if (res.no_data) {
        setNotice(
          `Sync completed — Google Search Console returned no query rows for this property for ${presetMeta.label.toLowerCase()} (${ukDate(res.window.start)} → ${ukDate(res.window.end)}). That is the provider’s real answer; nothing was invented and no insight was generated.`,
        );
      } else {
        setNotice(
          `Synced ${res.synced_rows} ${res.synced_rows === 1 ? 'query' : 'queries'} from Google Search Console (${ukDate(res.window.start)} → ${ukDate(res.window.end)}).`,
        );
      }
      if (res.insight) setSyncedInsight({ projectId, insight: res.insight });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['seo', 'search-analytics', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['seo', 'intelligence'] }),
      ]);
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.status === 503 || err.code === 'provider_unavailable')
      ) {
        setPanel({
          title: 'Awaiting connection — Google Search Console isn’t linked yet',
          body: 'Live syncs need the Google Search Console connection (service-account credentials), which isn’t configured for this environment yet. Nothing was fetched and nothing was stored — previously synced rows, if any, still show below.',
        });
      } else if (err instanceof ApiError && (err.status === 429 || err.code === 'rate_limited')) {
        setPanel({
          title: 'Sync limit reached for now',
          body: 'This workspace has hit its sync rate limit, so nothing was fetched and nothing was stored. Stored rows below still serve; try again as the window rolls over.',
        });
      } else if (err instanceof ApiError && (err.status === 502 || err.code === 'provider_error')) {
        setPanel({
          title: 'The Search Console request failed',
          body: `${err.message ? `${err.message} ` : ''}Nothing was stored — previously synced rows below are unaffected. Try again in a moment.`,
        });
      } else {
        setPanel({
          title: 'The sync could not be completed',
          body:
            err instanceof ApiError && err.message
              ? `${err.message} Nothing was stored — try again.`
              : 'The data service returned an unexpected response. Nothing was stored — try again.',
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Search Performance" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Search Performance" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }

  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Search Performance" />
        <EmptyState
          title="No properties to measure yet"
          teach="Search Console data lives inside an SEO project. Add your first domain on the Projects page, then come back here to sync clicks, impressions and positions straight from Google."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  // One stored window, aggregated by query (the server already does this;
  // the client repeats it so an older API answer can never show duplicates).
  const rows = aggregateGscRows(analyticsQ.data?.rows ?? []);
  const visibleRows = filterGscRows(rows, search);
  const lastSyncedAt = analyticsQ.data?.last_synced_at ?? null;
  const windowMeta = analyticsQ.data?.window ?? null;
  const availableTokens = new Set((analyticsQ.data?.windows ?? []).map((w) => w.token));
  const totalInWindow = windowMeta?.total_rows ?? rows.length;

  // Totals over the queries SHOWN (the selected window, after the search) —
  // computed, clearly scoped, never a whole-property claim. Avg CTR = total
  // clicks / total impressions; avg position is impressions-weighted so head
  // queries count for what they saw.
  const totals = gscTotals(visibleRows);
  const totalClicks = totals.clicks;
  const totalImpressions = totals.impressions;
  const avgCtr = totals.ctr;
  const avgPosition = totals.position;

  // The insight to render: this session's sync result wins (freshest), else
  // the stored rollup insight when it belongs to the selected project.
  const rollupOpp = intelligenceQ.data?.top_opportunity ?? null;
  const insight: SeoGscInsight | null =
    syncedInsight && syncedInsight.projectId === projectId
      ? syncedInsight.insight
      : rollupOpp && projectId !== null && rollupOpp.project_id === projectId
        ? { kind: 'keyword_opportunity', subject: rollupOpp.subject, body: rollupOpp.body }
        : null;

  // "Based on" evidence line — the stored query metrics behind the insight.
  const evidenceRow = topOpportunityRow(rows);
  const basedOn =
    rollupOpp && projectId !== null && rollupOpp.project_id === projectId
      ? `“${rollupOpp.subject}” — ${formatInt(rollupOpp.impressions)} impressions · ${formatInt(rollupOpp.clicks)} clicks · avg position ${rollupOpp.position.toFixed(1)} (Google Search Console, last 28 days)`
      : evidenceRow
        ? `“${evidenceRow.query}” — ${formatInt(evidenceRow.impressions)} impressions · ${formatInt(evidenceRow.clicks)} clicks · CTR ${formatCtr(evidenceRow.ctr)} · avg position ${evidenceRow.position.toFixed(1)} (Google Search Console, ${evidenceRow.date_start} → ${evidenceRow.date_end})`
        : 'the synced Google Search Console query metrics for this property (last 28 days)';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => {
          void select(Number(e.target.value));
          clearFeedback();
        }}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  const syncButton = canManage ? (
    <Button
      variant="primary"
      disabled={syncing}
      onClick={() => void handleSync()}
      title={`Pulls ${presetMeta.label.toLowerCase()} of query data from Google Search Console and stores it with source attribution`}
    >
      {syncing ? 'Syncing…' : 'Sync Now'}
    </Button>
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Search Performance"
        subtitle="Clicks, impressions and positions as Google measured them — synced from Search Console, stored with their source."
        actions={
          <>
            {projectSelector}
            {syncButton}
          </>
        }
      />

      {notice && (
        <div className="rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      {panel && (
        <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
          <div className="text-sm font-medium text-text-primary">{panel.title}</div>
          <p className="mt-1 text-sm text-text-secondary">{panel.body}</p>
        </div>
      )}

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel={project ? `Google Search Console — ${project.label}` : 'Google Search Console'}
        hero={<span data-tabular>{analyticsQ.isSuccess ? formatInt(totalClicks) : '—'}</span>}
        heroSub={`Clicks across the stored queries for ${presetMeta.label.toLowerCase()}${windowMeta ? ` (${ukDate(windowMeta.start)} → ${ukDate(windowMeta.end)})` : ''}. Licensed, source-attributed Google data — never a proprietary-index claim.`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{SOURCE_TAG}</Badge>
          <span className="text-xs text-text-tertiary">
            {lastSyncedAt
              ? `Last synced ${formatRelative(lastSyncedAt)}`
              : 'Never synced — no Search Console data stored for this property yet'}
          </span>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div role="group" aria-label="Date range" className="flex flex-wrap gap-1.5">
            {GSC_PRESETS.map((p) => {
              const active = p.token === preset;
              const hasData = availableTokens.has(p.token);
              return (
                <button
                  key={p.token}
                  type="button"
                  aria-pressed={active}
                  title={p.hint + (hasData ? '' : ' — not synced yet')}
                  onClick={() => {
                    setParam('range', p.token);
                    clearFeedback();
                  }}
                  className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-md border px-3 text-sm font-medium sm:min-h-0 sm:py-1.5 ${
                    active
                      ? 'border-ai bg-ai-wash text-text-primary'
                      : 'border-border-default bg-canvas text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {p.label}
                  {!hasData && analyticsQ.isSuccess && (
                    <span className="text-[10px] font-normal text-text-tertiary">· no data</span>
                  )}
                </button>
              );
            })}
          </div>
          <label className="flex w-full items-center gap-2 sm:w-auto">
            <span className="sr-only">Search queries</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setParam('q', e.target.value)}
              placeholder="Search queries"
              aria-label="Search queries"
              className="h-control min-h-[44px] w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 sm:min-h-0 sm:w-64"
            />
          </label>
        </div>
        {windowMeta && (
          <p className="text-xs text-text-tertiary" data-testid="gsc-window-line">
            {presetMeta.label}: {ukDate(windowMeta.start)} → {ukDate(windowMeta.end)} (Google’s finalised days)
            {' · '}
            {search.trim() !== ''
              ? `${formatInt(visibleRows.length)} of ${formatInt(rows.length)} queries match “${search.trim()}”`
              : `${formatInt(rows.length)} ${rows.length === 1 ? 'query' : 'queries'}`}
            {totalInWindow > rows.length ? ` (top ${formatInt(rows.length)} of ${formatInt(totalInWindow)} by clicks)` : ''}
          </p>
        )}

        {analyticsQ.isPending ? (
          <LoadingState rows={4} />
        ) : analyticsQ.error ? (
          <ErrorState error={analyticsQ.error} onRetry={() => void analyticsQ.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={lastSyncedAt ? `No ${presetMeta.label.toLowerCase()} data stored yet` : 'No Search Console data stored yet'}
            teach={`Sync ${project?.domain ?? 'this property'} to pull ${presetMeta.label.toLowerCase()} of real query metrics — clicks, impressions, CTR and position, exactly as Google reports them. If Search Console isn’t connected yet, the sync will say so honestly.`}
            action={
              canManage ? (
                <Button variant="primary" disabled={syncing} onClick={() => void handleSync()}>
                  {syncing ? 'Syncing…' : 'Sync Now'}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <Card className="p-4">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Metric label="Clicks" value={formatInt(totalClicks)} />
                <Metric label="Impressions" value={formatInt(totalImpressions)} />
                <Metric
                  label="Avg CTR"
                  value={avgCtr !== null ? formatCtr(avgCtr) : '—'}
                  hint="Total clicks ÷ total impressions across the stored top queries"
                />
                <Metric
                  label="Avg position"
                  value={avgPosition !== null ? avgPosition.toFixed(1) : '—'}
                  hint="Impressions-weighted average across the stored top queries"
                />
              </div>
              <p className="mt-3 text-[11px] text-text-tertiary">
                Totals are computed across the {formatInt(visibleRows.length)} stored{' '}
                {visibleRows.length === 1 ? 'query' : 'queries'} shown for {presetMeta.label.toLowerCase()}
                {search.trim() !== '' ? ' that match the search' : ''} — not a whole-property total.
              </p>
            </Card>

            {insight && <InsightStrip insight={insight} basedOn={basedOn} />}

            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <SortableTable
                rows={visibleRows}
                columns={GSC_COLUMNS}
                getRowKey={gscRowKey}
                minWidthClass="min-w-[640px]"
                caption="Stored Search Console top queries with clicks, impressions, CTR and position"
              />
            </div>
          </>
        )}
      </EngineDashboard>
    </div>
  );
}
